/**
 * 移入批次抽屉：解析结果**按包牌一行**（Q8）——包牌号、分拣码、当前所在批次、包裹数与目标批次下拉。
 * 下拉选项为该包牌自己的候选批次（后端已按「目的地更精确 → 创建时间早」排序）；一个包牌被多个目的地的批次覆盖时全部列出。
 */
import { Select, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { BatchBriefApi, MoveInBagItemApi } from '../graphql/queries/previewSortingBatchMove';

export interface MoveInBagTableProps {
    items: MoveInBagItemApi[];
    /** 包牌号 → 已选目标批次 id */
    selections: Record<string, number>;
    disabled: boolean;
    onSelect: (bagNumber: string, batchId: number) => void;
}

type Row = MoveInBagItemApi & { key: string };

export const MoveInBagTable: React.FC<MoveInBagTableProps> = ({ items, selections, disabled, onSelect }) => {
    const { t } = useTranslation();
    const none = t('wms.sortingBatch.common.none');
    const separator = String(t('wms.sortingBatch.common.separator'));

    const optionLabel = (item: MoveInBagItemApi, b: BatchBriefApi): string => [
        b.batchName,
        t(`wms.sortingBatch.status.${b.status}`),
        b.destination === item.destination
            ? t('wms.sortingBatch.moveIn.sameDestination')
            : t('wms.sortingBatch.moveIn.parentDestination', { destination: b.destination }),
    ].join(' · ');

    const columns: ColumnsType<Row> = [
        {
            title: t('wms.sortingBatch.moveIn.columns.bag'),
            dataIndex: 'bagNumber',
            render: (v: string) => <span className="font-mono text-[12.5px]">{v}</span>,
        },
        {
            title: t('wms.sortingBatch.moveIn.columns.sortingCode'),
            dataIndex: 'sortingCode',
            render: (v: string | null) => <span className="font-mono text-xs">{v || none}</span>,
        },
        {
            title: t('wms.sortingBatch.moveIn.columns.currentBatch'),
            key: 'currentBatches',
            render: (_: unknown, r) => (
                <span className="font-mono text-xs">{r.currentBatches.length ? r.currentBatches.map((b) => b.batchName).join(separator) : none}</span>
            ),
        },
        {
            title: t('wms.sortingBatch.moveIn.columns.parcelCount'),
            dataIndex: 'parcelCount',
            align: 'center',
        },
        {
            title: t('wms.sortingBatch.moveIn.targetBatch'),
            key: 'target',
            width: 360,
            render: (_: unknown, r) => (
                <Select
                    className="w-full"
                    value={selections[r.bagNumber]}
                    disabled={disabled}
                    options={r.candidates.map((b) => ({ value: b.id, label: optionLabel(r, b) }))}
                    onChange={(v: number) => onSelect(r.bagNumber, v)}
                />
            ),
        },
    ];

    const data: Row[] = items.map((i) => ({ ...i, key: i.bagNumber }));
    return <Table<Row> size="small" bordered columns={columns} dataSource={data} pagination={false} />;
};
